// Floating action button (mobile only). Tapping the + opens a
// bottom-sheet menu so creation/upload actions line up with the rest
// of the mobile UI language (Move, Export, Item actions all use
// BottomSheet). On desktop the FAB is hidden — those actions live in
// the Topbar / Sidebar.

import { useState } from "react";
import { Camera, FolderPlus, Plus, Upload } from "lucide-react";
import { cn } from "../../lib/utils";
import { BottomSheet } from "../ui/bottom-sheet";

interface Props {
  onNewFolder: () => void;
  onUpload: () => void;
  onCapturePhoto?: () => void;
  className?: string;
}

export function Fab({
  onNewFolder,
  onUpload,
  onCapturePhoto,
  className,
}: Props) {
  const [open, setOpen] = useState(false);

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <>
      <button
        type="button"
        aria-label="Add"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={cn(
          "fixed bottom-5 right-5 z-30 grid h-14 w-14 place-items-center rounded-full bg-accent text-primary-foreground shadow-xl md:hidden",
          "bottom-[calc(env(safe-area-inset-bottom)+20px)]",
          className,
        )}
      >
        <Plus size={22} />
      </button>

      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        title="Add to this folder"
        className="p-0 md:p-0"
      >
        <ul className="flex flex-col py-1">
          <Item
            icon={<Upload className="h-5 w-5" />}
            label="Upload files"
            onClick={run(onUpload)}
          />
          {onCapturePhoto && (
            <Item
              icon={<Camera className="h-5 w-5" />}
              label="Take photo"
              onClick={run(onCapturePhoto)}
            />
          )}
          <li className="my-1 h-px bg-border" aria-hidden="true" />
          <Item
            icon={<FolderPlus className="h-5 w-5" />}
            label="New folder"
            onClick={run(onNewFolder)}
          />
        </ul>
      </BottomSheet>
    </>
  );
}

function Item({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-text-soft hover:bg-accent-bg hover:text-text active:bg-accent-bg"
      >
        <span className="text-text-dim">{icon}</span>
        <span className="flex-1 text-base">{label}</span>
      </button>
    </li>
  );
}
